/**
 * SalesCodeLanding - Coupling WNC
 *
 * WNC-localized marketing landing page for sales code QR scans on couplingwnc.com.
 * Queries the GraphQL API for partner/campaign context, then shows
 * an informational page that funnels users to app.cplng.org/s/{code}
 * where click tracking and attribution happen.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { Hero, Features, CTA, Footer, SEO, PLAY_STORE_URL, APP_STORE_URL } from '@coupling/ui';
import Header from '../components/Header';
import { seoConfig } from '../config/seo';

const appUrl = import.meta.env.VITE_APP_URL || 'https://app.cplng.org';

const SALES_CODE_LANDING = gql`
  query SalesCodeLanding($code: String!) {
    salesCodeLanding(code: $code) {
      campaignName
      partnerName
    }
  }
`;

const features = [
  {
    icon: '🎁',
    title: 'Earn Token Rewards',
    description:
      'Get reward tokens from WNC merchants every time you shop in Asheville, West Asheville, South Slope, and beyond.',
  },
  {
    icon: '🔄',
    title: 'Spend Across WNC Businesses',
    description:
      'Use your tokens at any participating WNC merchant. Unlike traditional loyalty points, these work across the whole network.',
  },
  {
    icon: '🏔️',
    title: 'Keep It Local',
    description:
      'Every token spent keeps value circulating in Western North Carolina. Join a network that rewards shopping local.',
  },
];

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'Benefits', href: '/#benefits' },
      { label: 'How it Works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/#pricing' },
    ],
  },
  {
    title: 'Mobile App',
    links: [
      { label: 'Get the App', href: '/app' },
      { label: 'Google Play', href: PLAY_STORE_URL },
      { label: 'App Store', href: APP_STORE_URL },
    ],
  },
  {
    title: 'Network',
    links: [
      { label: 'Coupling (Global)', href: 'https://cplng.org' },
      { label: 'Blog', href: '/blog' },
    ],
  },
  {
    title: 'Legal',
    links: [{ label: 'Privacy Policy', href: '/privacy' }],
  },
];

const SalesCodeLanding: React.FC = () => {
  const { code } = useParams<{ code: string }>();

  const { data, loading, error } = useQuery(SALES_CODE_LANDING, {
    variables: { code },
    skip: !code,
    fetchPolicy: 'network-only',
  });

  const appSignupUrl = code ? `${appUrl}/s/${code}` : `${appUrl}/signup`;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
        </div>
        <Footer
          description="Helping WNC merchants create interconnected reward networks that strengthen mountain communities."
          columns={footerColumns}
          copyright={`\u00A9 ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
        />
      </div>
    );
  }

  // Error or invalid code
  if (error || !data?.salesCodeLanding) {
    return (
      <div className="min-h-screen bg-white">
        <SEO
          title={`Invalid Link - ${seoConfig.siteName}`}
          description="This link is invalid or has expired."
          canonical={seoConfig.siteUrl}
          ogImage={seoConfig.defaultOgImage}
        />
        <Header />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-4">Invalid Link</h1>
            <p className="text-neutral-600 mb-8">
              This link is invalid or has expired. Please check with the person who shared it with you.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-6 py-3 text-base font-medium rounded-full text-white bg-brand-primary hover:bg-brand-primary-dark transition-colors"
            >
              Go to Homepage
            </a>
          </div>
        </div>
        <Footer
          description="Helping WNC merchants create interconnected reward networks that strengthen mountain communities."
          columns={footerColumns}
          copyright={`\u00A9 ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
        />
      </div>
    );
  }

  const landing = data.salesCodeLanding;
  const pageTitle = landing.partnerName
    ? `${landing.partnerName} + ${seoConfig.siteName}`
    : `Welcome to ${seoConfig.siteName}`;

  const heroBadge = landing.partnerName
    ? `Brought to you by ${landing.partnerName}`
    : 'WNC Merchant Rewards';

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={pageTitle}
        description={`Join ${seoConfig.siteName} and start earning token rewards from WNC merchants. ${landing.campaignName || ''}`}
        canonical={`${seoConfig.siteUrl}/s/${code}`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/s/${code}`}
      />
      <Header />

      {/* Hero Section */}
      <Hero
        badge={heroBadge}
        headline="Token Rewards for Asheville & WNC"
        description="Earn and spend reward tokens at local WNC businesses. Sign up in seconds and start collecting rewards from merchants across Western North Carolina."
        primaryCta="Get Started Free"
        onPrimaryCta={() => window.open(appSignupUrl, '_blank', 'noopener,noreferrer')}
        image={
          <img
            src="/product/mobile-wallet-cluster.webp"
            alt="Coupling mobile wallet product shot showing balances, rewards, and places to spend"
            className="w-full h-auto rounded-[2rem] shadow-2xl"
            width={1400}
            height={1000}
          />
        }
        background="gradient"
      />

      {/* Features Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              A simple way to earn and use rewards across WNC businesses.
            </p>
          </div>
          <Features features={features} columns={3} />
        </div>
      </section>

      {/* Bottom CTA */}
      <CTA
        headline="Ready to Join the WNC Network?"
        description="Sign up for free and start earning token rewards from WNC merchants today."
        primaryCta="Get Started Free"
        onPrimaryCta={() => window.open(appSignupUrl, '_blank', 'noopener,noreferrer')}
        background="gradient"
        size="lg"
        align="center"
      />

      <Footer
        description="Helping WNC merchants create interconnected reward networks that strengthen mountain communities."
        columns={footerColumns}
        copyright={`\u00A9 ${new Date().getFullYear()} Coupling WNC. Part of the Coupling network.`}
      />
    </div>
  );
};

export default SalesCodeLanding;
